import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { ChatCircle, Star, X, PaperPlaneRight } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'general', label: 'General' },
  { id: 'bug', label: 'Bug Report' },
  { id: 'feature', label: 'Feature Request' },
  { id: 'content', label: 'Content Tools' },
  { id: 'billing', label: 'Billing' },
];

export const FeedbackWidget = () => {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [category, setCategory] = useState('general');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async () => {
    if (rating === 0) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/feedback/submit`, {
        rating, category, message,
        page: window.location.pathname,
      });
      setSubmitted(true);
      setTimeout(() => { setOpen(false); setSubmitted(false); setRating(0); setMessage(''); setCategory('general'); }, 2500);
    } catch {}
    setSubmitting(false);
  };

  return (
    <>
      {/* Floating Button */}
      <button onClick={() => setOpen(true)} data-testid="feedback-widget-btn"
        className="fixed bottom-20 lg:bottom-6 right-6 z-50 w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-110"
        style={{ background: '#c8f060', color: '#080808', display: open ? 'none' : 'flex' }}>
        <ChatCircle size={22} weight="fill" />
      </button>

      {/* Modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-20 lg:bottom-6 right-6 z-50 w-80 rounded-2xl overflow-hidden shadow-2xl"
            style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <p className="text-sm font-semibold" style={{ color: '#f5f0e8' }}>Share Feedback</p>
              <button onClick={() => setOpen(false)} data-testid="feedback-close" className="p-1 rounded-lg hover:bg-white/5">
                <X size={16} style={{ color: 'rgba(245,240,232,0.4)' }} />
              </button>
            </div>

            {submitted ? (
              <div className="p-8 text-center">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(200,240,96,0.1)' }}>
                  <Star size={24} weight="fill" style={{ color: '#c8f060' }} />
                </div>
                <p className="text-sm font-medium" style={{ color: '#f5f0e8' }}>Thank you!</p>
                <p className="text-xs mt-1" style={{ color: 'rgba(245,240,232,0.3)' }}>Your feedback helps us improve.</p>
              </div>
            ) : (
              <div className="p-5 space-y-4">
                {/* Stars */}
                <div>
                  <p className="text-xs mb-2" style={{ color: 'rgba(245,240,232,0.4)' }}>How's your experience?</p>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map(s => (
                      <button key={s} data-testid={`feedback-star-${s}`}
                        onMouseEnter={() => setHoverRating(s)} onMouseLeave={() => setHoverRating(0)}
                        onClick={() => setRating(s)}
                        className="p-1 transition-transform hover:scale-110">
                        <Star size={24} weight={s <= (hoverRating || rating) ? 'fill' : 'regular'}
                          style={{ color: s <= (hoverRating || rating) ? '#f59e0b' : 'rgba(255,255,255,0.12)' }} />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Category */}
                <div>
                  <p className="text-xs mb-2" style={{ color: 'rgba(245,240,232,0.4)' }}>Category</p>
                  <div className="flex flex-wrap gap-1.5">
                    {CATEGORIES.map(c => (
                      <button key={c.id} data-testid={`feedback-cat-${c.id}`}
                        onClick={() => setCategory(c.id)}
                        className="px-3 py-1.5 rounded-full text-[10px] font-medium transition-all"
                        style={{
                          background: category === c.id ? 'rgba(200,240,96,0.1)' : 'rgba(255,255,255,0.03)',
                          border: `1px solid ${category === c.id ? 'rgba(200,240,96,0.25)' : 'rgba(255,255,255,0.06)'}`,
                          color: category === c.id ? '#c8f060' : 'rgba(245,240,232,0.4)',
                        }}>
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Message */}
                <div>
                  <textarea data-testid="feedback-message" value={message} onChange={e => setMessage(e.target.value)}
                    placeholder="Tell us more (optional)..."
                    rows={3} className="w-full py-2.5 px-3 rounded-lg text-sm resize-none"
                    style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)', color: '#f5f0e8' }} />
                </div>

                {/* Submit */}
                <button onClick={submit} disabled={rating === 0 || submitting} data-testid="feedback-submit"
                  className="w-full py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all"
                  style={{
                    background: rating > 0 ? '#c8f060' : 'rgba(255,255,255,0.05)',
                    color: rating > 0 ? '#080808' : 'rgba(245,240,232,0.2)',
                    opacity: submitting ? 0.6 : 1,
                  }}>
                  <PaperPlaneRight size={14} weight="fill" />
                  {submitting ? 'Sending...' : 'Send Feedback'}
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
